import { IReferralType } from "../../Services/ReferralTypes";
import IFirm from "../../interfaces/models/IFirm";

export type ReferralPersonOption = {
  id: string;
  name: string;
  lastName?: string;
  legacyId?: string | number;
};

type FirmChoice = {
  id: string;
  legacyId?: string | number;
  firmName?: string;
  name?: string;
  shortName?: string;
};

type SavedParty = {
  cosmosId?: string;
  legacyId?: string | number;
  name?: string;
};

function sameText(left: unknown, right: unknown): boolean {
  const a = String(left ?? "").trim().toLowerCase();
  const b = String(right ?? "").trim().toLowerCase();
  return a !== "" && a === b;
}

function personLabel(person: ReferralPersonOption): string {
  return [person.name, person.lastName].filter(Boolean).join(" ").trim();
}

export function asPersonOptions(rows: unknown): ReferralPersonOption[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.flatMap((row) => {
    const person = row as ReferralPersonOption;
    const id = String(person?.id ?? "").trim();
    if (!id) {
      return [];
    }
    return [{
      id,
      name: String(person.name ?? ""),
      lastName: person.lastName ? String(person.lastName) : undefined,
      legacyId: person.legacyId,
    }];
  });
}

export function resolveFirmOptionId(firms: FirmChoice[], selected: SavedParty): string {
  const cosmosId = String(selected.cosmosId ?? "").trim();
  if (cosmosId && firms.some((firm) => firm.id === cosmosId)) {
    return cosmosId;
  }
  const legacyId = String(selected.legacyId ?? "").trim();
  if (legacyId) {
    const byLegacy = firms.find((firm) => String(firm.legacyId ?? "").trim() === legacyId);
    if (byLegacy?.id) {
      return byLegacy.id;
    }
  }
  const byName = firms.find((firm) => sameText(firm.firmName, selected.name) || sameText(firm.name, selected.name) || sameText(firm.shortName, selected.name));
  if (byName?.id) {
    return byName.id;
  }
  return cosmosId;
}

export function resolvePersonOptionId(people: ReferralPersonOption[], selected: SavedParty): string {
  const cosmosId = String(selected.cosmosId ?? "").trim();
  if (cosmosId && people.some((person) => person.id === cosmosId)) {
    return cosmosId;
  }
  const legacyId = String(selected.legacyId ?? "").trim();
  if (legacyId) {
    const byLegacy = people.find((person) => String(person.legacyId ?? "").trim() === legacyId);
    if (byLegacy?.id) {
      return byLegacy.id;
    }
  }
  const byName = people.find((person) => sameText(personLabel(person), selected.name));
  if (byName?.id) {
    return byName.id;
  }
  return cosmosId;
}

export function resolveReferralTypeOptionId(types: IReferralType[], selected: { referralTypeId?: unknown; legacyType?: unknown; label?: string }): string {
  const candidates = [selected.referralTypeId, selected.legacyType]
    .map((value) => String(value ?? "").trim())
    .filter((value) => value !== "" && Number.isFinite(Number(value)));
  for (const candidate of candidates) {
    const match = types.find((type) => String(type.typeId) === candidate || Number(type.typeId) === Number(candidate));
    if (match) {
      return String(match.typeId);
    }
  }
  const byLabel = types.find((type) => sameText(type.description, selected.label));
  if (byLabel) {
    return String(byLabel.typeId);
  }
  return candidates[0] ? String(Number(candidates[0])) : "";
}

export function ensureFirmOption(firms: IFirm[], id: string, label: string): IFirm[] {
  const firmId = id.trim();
  if (!firmId || firms.some((firm) => firm.id === firmId)) {
    return firms;
  }
  const name = label.trim() || firmId;
  return [...firms, { id: firmId, name, firmName: name, shortName: name } as IFirm];
}

export function ensurePersonOption(people: ReferralPersonOption[], id: string, label: string): ReferralPersonOption[] {
  const personId = id.trim();
  if (!personId || people.some((person) => person.id === personId)) {
    return people;
  }
  const text = label.trim() || personId;
  const [name, ...rest] = text.split(" ");
  return [{ id: personId, name, lastName: rest.join(" ") || undefined }, ...people];
}

export function ensureTypeOption(types: IReferralType[], typeId: string, label: string): IReferralType[] {
  const id = typeId.trim();
  if (!id || types.some((type) => String(type.typeId) === id)) {
    return types;
  }
  return [...types, { id: `saved-${id}`, legacyId: "", typeId: id, description: label.trim() || "Current type", isActive: 1, displayOrder: 0 }];
}

export function keepUntouchedChoice(current: string, loaded: string, resolved: string): string {
  if (current !== loaded) {
    return current;
  }
  return resolved || current;
}
